import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";

type CollapsibleTextProps = {
  children: React.ReactNode;
  maxRows?: 5;
};

export const CollapsibleText: React.FC<CollapsibleTextProps> = ({
  children,
  maxRows,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isContentFullyVisible, setIsContentFullyVisible] = useState(true);
  const [heights, setHeights] = useState({ collapsed: 0, full: 0 });
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;

    const originalStyle = contentRef.current.style.cssText;
    const originalClass = contentRef.current.className;

    // Force to full height to measure full height without transitions
    contentRef.current.className = "markdown";
    contentRef.current.style.cssText =
      "max-height: none !important; display: block !important; transition: none !important;";
    const fullHeight = contentRef.current.scrollHeight;

    // Force line-clamp to measure accurate collapsed height
    contentRef.current.className = cn("markdown", {
      "line-clamp-5": maxRows === 5,
    });
    contentRef.current.style.cssText = "transition: none !important;";
    const collapsedHeight = contentRef.current.clientHeight;

    setHeights({ collapsed: collapsedHeight, full: fullHeight });
    setIsContentFullyVisible(fullHeight <= collapsedHeight);

    // Restore
    contentRef.current.style.cssText = originalStyle;
    contentRef.current.className = originalClass;
  }, [children, maxRows]);

  const handleExpand = () => {
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 200);
  };

  return (
    <div>
      <div
        ref={contentRef}
        className={cn(
          "markdown overflow-hidden transition-[max-height] duration-200 ease-in-out",
          {
            "line-clamp-5": maxRows === 5 && !isExpanded && !isTransitioning,
          },
        )}
        style={{
          maxHeight: heights.full
            ? isExpanded
              ? `${heights.full}px`
              : `${heights.collapsed}px`
            : undefined,
        }}
      >
        {children}
      </div>
      {maxRows && !isExpanded && !isContentFullyVisible && (
        <Button
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={handleExpand}
        >
          Show More
        </Button>
      )}
      {maxRows && isExpanded && (
        <Button
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={handleCollapse}
        >
          Show Less
        </Button>
      )}
    </div>
  );
};
